import { configureStore } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import { persistStore, persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import { combineReducers } from '@reduxjs/toolkit'
import type { ThunkAction, Action } from '@reduxjs/toolkit'

// Import feature slices
import authSlice from '@/features/auth/authSlice'
import cartSlice from '@/features/cart/cartSlice'
import cartItemsSlice from '@/features/cart/cartItemsSlice'
import customerSlice from '@/features/customer/customerSlice'
import discountSlice from '@/features/discount/discountSlice'
import csFlowSlice from '@/features/csFlow/csFlowSlice'
import csCartSlice from '@/features/csFlow/csCartSlice'
import menuSlice from '@/features/menu/menuSlice'
import ordersSlice from '@/features/orders/ordersSlice'
import ordersFilterSlice from '@/features/ordersFilter/ordersFilterSlice'
import profileSlice from '@/features/profile/profileSlice'
import settingsSlice from '@/features/settings/settingsSlice'
import shiftSlice from '@/features/shift/shiftSlice'
import tablesSlice from '@/features/tables/tablesSlice'
import paymentSlice from '@/features/payment/paymentSlice'
import productDetailsSlice from '@/features/product/productDetailsSlice'
import navigationSlice from '@/features/navigation/navigationSlice'

// Import API services
import { authApi } from '@/features/auth/authAPI'
import { menuApi } from '@/features/menu/menuAPI'
import { ordersApi } from '@/features/orders/ordersAPI'
import { cartApi } from '@/features/cart/cartAPI'
import { tablesApi } from '@/features/tables/tablesAPI'
import { profileApi } from '@/features/profile/profileAPI'
import { customerApi } from '@/features/customer/customerAPI'
import { discountApi } from '@/features/discount/discountAPI'
import { csFlowApi } from '@/features/csFlow/csFlowAPI'
import offersReducer from '@/features/offers/offersSlice'
import { offersListenerMiddleware } from '@/features/offers/offersListeners'

// Persist configuration
const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth', 'settings', 'profile'], // Only persist these slices
  blacklist: ['cart', 'cartItems', 'csCart', 'menu', 'orders', 'tables', 'payment', 'shift', 'offers'], // Don't persist these
}

// Root reducer
const rootReducer = combineReducers({
  // Feature slices
  auth: authSlice,
  cart: cartSlice,
  cartItems: cartItemsSlice,
  customer: customerSlice,
  discount: discountSlice,
  csFlow: csFlowSlice,
  csCart: csCartSlice,
  menu: menuSlice,
  orders: ordersSlice,
  ordersFilter: ordersFilterSlice,
  profile: profileSlice,
  settings: settingsSlice,
  shift: shiftSlice,
  tables: tablesSlice,
  payment: paymentSlice,
  productDetails: productDetailsSlice,
  navigation: navigationSlice,
  offers: offersReducer,
  
  // API slices
  [authApi.reducerPath]: authApi.reducer,
  [menuApi.reducerPath]: menuApi.reducer,
  [ordersApi.reducerPath]: ordersApi.reducer,
  [cartApi.reducerPath]: cartApi.reducer,
  [tablesApi.reducerPath]: tablesApi.reducer,
    [profileApi.reducerPath]: profileApi.reducer,
    [customerApi.reducerPath]: customerApi.reducer,
    [discountApi.reducerPath]: discountApi.reducer,
    [csFlowApi.reducerPath]: csFlowApi.reducer,
})

// Persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer)

// Configure store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    })
    .prepend(offersListenerMiddleware.middleware)
    .concat(
      authApi.middleware,
      menuApi.middleware,
      ordersApi.middleware,
      cartApi.middleware,
      tablesApi.middleware,
      profileApi.middleware,
      customerApi.middleware,
      discountApi.middleware,
      csFlowApi.middleware,
    ) as any,
  devTools: import.meta.env.DEV,
})

// Setup listeners for RTK Query
setupListeners(store.dispatch)

// Create persistor
export const persistor = persistStore(store)

// Export types
export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>

// Export store
export default store
